import {
  Box,
  Stack,
  Card,
  Body,
  Button,
  Upselling,
  UmbrellaIcon,
  FormSection,
  FormRow,
  FeedbackBlock,
  useIsMobileLayout,
  ContentRow,
  Dialog,
  InfoIcon,
  Space,
} from "@dbn/omnichannel-design-system-g4p";
import {
  LocaleKey,
  useFormatMessage,
  useFormatMoneyAmountValue,
  useFormatPercentageLike,
  useFormatRichMessage2,
} from "../../intl";
import * as offerListApi from "./api";
import * as standardLoanApi from "../api";
import { useQuery, useCommand } from "../../useAPI";
import { constNull, pipe } from "fp-ts/function";
import { array, taskEither, option, boolean } from "fp-ts";
import * as remoteData from "../../RemoteData";
import { Reader } from "fp-ts/Reader";
import { IO } from "fp-ts/IO";
import { NonEmptyArray } from "fp-ts/NonEmptyArray";
import { ReadOnlyFieldToValue } from "../../Common/ReadOnlyField/ReadOnlyFieldToValue";
import { useScrollTopOnChange } from "../../Common/useScrollTopOnChange";
import { useState } from "react";

type Props = {
  onSelectOffer: Reader<standardLoanApi.GenericLoanResponseOutput, void>;
  onSelectOfferSocialInsurance: () => void;
  onBack: IO<void>;
  offerState: Exclude<offerListApi.CheckOfferStatus, "PENDING">;
};

export function Counteroffer(props: Props) {
  const formatMessage = useFormatMessage();
  const formatRichMessage = useFormatRichMessage2();
  useScrollTopOnChange("smooth");
  const formatMoneyAmount = useFormatMoneyAmountValue();
  const formatPercentage = useFormatPercentageLike();
  const saveSelectedOffer = useCommand(offerListApi.saveSelectedOffer);
  const saveSelectedOfferSocialInsurance = useCommand(
    offerListApi.saveSelectedOfferSocialInsurance
  );
  const [counterOffer] = useQuery(offerListApi.getCounterOffers);
  const isMobileLayout = useIsMobileLayout();
  const [moreInfo, setMoreInfo] = useState<boolean>(false);

  const renderOffers = (
    offers: NonEmptyArray<standardLoanApi.GenericLoanOffer>
  ) =>
    pipe(
      offers,
      array.map(offer => (
        <Card grow={isMobileLayout}>
          <Stack width="100%" units={8} column>
            <Body size="big" weight="medium">
              {pipe(
                offer.offerType,
                option.fold(
                  () =>
                    formatMessage(
                      "StandardLoan.CounterOffer.counterOfferTitle"
                    ),
                  val =>
                    val === "COUNTER_OFFER"
                      ? formatMessage(
                          "StandardLoan.CounterOffer.counterOfferTitle"
                        )
                      : formatMessage(
                          `StandardLoan.CounterOffer.${val}.counterOfferTitle` as LocaleKey
                        )
                )
              )}
            </Body>
            {pipe(
              offer.newMoneyAmount,
              option.fold(
                () => (
                  <ReadOnlyFieldToValue
                    size="big"
                    label={formatMessage(
                      "StandardLoan.CounterOffer.loanAmount"
                    )}
                    value={formatMoneyAmount({
                      amount: offer.borrowedAmount,
                      currency: offer.currencyCode,
                    })}
                    valueSize={"big"}
                    labelWeight={"bold"}
                  />
                ),
                amount => (
                  <FormSection>
                    <FormRow type="full">
                      <ReadOnlyFieldToValue
                        size="big"
                        label={formatMessage(
                          "StandardLoan.OfferApproved.ChooseCard.refinancingAmount"
                        )}
                        value={formatMoneyAmount({
                          amount,
                          currency: offer.currencyCode,
                        })}
                        valueSize={"big"}
                        labelWeight={"bold"}
                      />
                    </FormRow>
                    <FormRow type="full">
                      <ReadOnlyFieldToValue
                        size="medium"
                        label={formatMessage(
                          "StandardLoan.OfferApproved.ChooseCard.loanAmount"
                        )}
                        value={formatMoneyAmount({
                          amount: offer.borrowedAmount,
                          currency: offer.currencyCode,
                        })}
                        valueSize={"big"}
                        labelWeight={"bold"}
                      />
                    </FormRow>
                  </FormSection>
                )
              )
            )}

            <Stack units={8}>
              <ReadOnlyFieldToValue
                size="medium"
                label={formatMessage(
                  "StandardLoan.OfferApproved.OfferReview.monthlyInstallment"
                )}
                value={formatMoneyAmount({
                  amount: offer.installmentAmount,
                  currency: offer.currencyCode,
                })}
                labelWeight={"bold"}
              />
              <ReadOnlyFieldToValue
                size="medium"
                label={formatMessage(
                  "StandardLoan.OfferApproved.OfferReview.durationOfInstallments"
                )}
                value={formatMessage(
                  "StandardLoan.OfferApproved.OfferReview.durationOfInstallmentsValue",
                  {
                    duration: offer.tenor,
                  }
                )}
                labelWeight={"bold"}
              />
              <ReadOnlyFieldToValue
                size="medium"
                label={formatMessage(
                  "StandardLoan.OfferApproved.OfferReview.interestRate"
                )}
                value={formatMessage(
                  "StandardLoan.OfferApproved.OfferReview.interestRateValue",
                  {
                    rate: formatPercentage(offer.interestRateDecimal),
                  }
                )}
                labelWeight={"bold"}
              />
              {pipe(
                offer.cpimonthlyAmount,
                option.fold(constNull, cpimonthlyAmount => (
                  <ReadOnlyFieldToValue
                    size="medium"
                    label={formatMessage(
                      "StandardLoan.OfferApproved.OfferReview.cpiMonthlyAmount"
                    )}
                    value={formatMoneyAmount({
                      amount: cpimonthlyAmount,
                      currency: offer.currencyCode,
                    })}
                    labelWeight={"bold"}
                  />
                ))
              )}
              {pipe(
                offer.apr,
                option.fold(constNull, apr => (
                  <ReadOnlyFieldToValue
                    size="medium"
                    label={formatMessage(
                      "StandardLoan.OfferApproved.OfferReview.apr"
                    )}
                    value={formatMessage("Percentage.pa", {
                      percentage: formatMessage("Percentage", {
                        value: apr,
                      }),
                    })}
                    labelWeight={"bold"}
                  />
                ))
              )}
              {pipe(
                offer.totalDueAmount,
                option.fold(constNull, totalDueAmount => (
                  <ReadOnlyFieldToValue
                    size="medium"
                    label={formatMessage(
                      "StandardLoan.OfferApproved.OfferReview.totalDueAmount"
                    )}
                    value={formatMoneyAmount({
                      amount: totalDueAmount,
                      currency: offer.currencyCode,
                    })}
                    labelWeight={"bold"}
                  />
                ))
              )}
            </Stack>
            {pipe(
              offer.cpiSelectedName,
              option.fold(constNull, cpiName =>
                pipe(
                  offer.cpimonthlyAmount,
                  option.fold(constNull, () => (
                    <Upselling
                      included
                      content={formatMessage(
                        "StandardLoan.OfferApproved.ChooseCard.insurance",
                        {
                          insurance: formatMessage(cpiName),
                        }
                      )}
                      icon={UmbrellaIcon}
                    />
                  ))
                )
              )
            )}
            <Space units={2} />
            <Stack width="100%" column>
              <Button
                type="button"
                label={formatMessage(
                  `StandardLoan.OfferApproved.ChooseCard.chooseOption.MoreInfo.${pipe(
                    offer.offerType,
                    option.getOrElse(() => "COUNTER_OFFER")
                  )}` as LocaleKey
                )}
                variant="text"
                size="small"
                action={() => {
                  setMoreInfo(true);
                }}
              />
              {moreInfo && (
                <Dialog
                  variant="center"
                  size="medium"
                  title={formatMessage(
                    `StandardLoan.OfferApproved.ChooseCard.chooseOption.MoreInfo.title.${pipe(
                      offer.offerType,
                      option.getOrElse(() => "COUNTER_OFFER")
                    )}` as LocaleKey
                  )}
                  onDismiss={option.some(() => setMoreInfo(false))}
                  actions={[]}
                  icon={InfoIcon}
                >
                  <Box
                    column
                    shrink
                    style={{ marginLeft: "6px", marginRight: "6px" }}
                  >
                    <Body size={"medium"} weight={"regular"}>
                      {formatRichMessage(
                        `StandardLoan.OfferApproved.ChooseCard.chooseOption.MoreInfo.body.${pipe(
                          offer.offerType,
                          option.getOrElse(() => "COUNTER_OFFER")
                        )}` as LocaleKey
                      )}
                    </Body>
                  </Box>
                </Dialog>
              )}
              <Space units={4} />
              <Button
                label={formatMessage(
                  "StandardLoan.OfferApproved.ChooseCard.chooseOption"
                )}
                variant="primary"
                size="default"
                action={() => {
                  pipe(
                    offer.offerType,
                    option.fold(
                      () =>
                        pipe(
                          saveSelectedOffer(offer),
                          taskEither.chain(selectedOffer =>
                            taskEither.fromIO(() =>
                              props.onSelectOffer(selectedOffer)
                            )
                          )
                        )(),
                      val =>
                        val === "COUNTER_OFFER"
                          ? pipe(
                              saveSelectedOffer(offer),
                              taskEither.chain(selectedOffer =>
                                taskEither.fromIO(() =>
                                  props.onSelectOffer(selectedOffer)
                                )
                              )
                            )()
                          : pipe(
                              saveSelectedOfferSocialInsurance(offer),
                              taskEither.chain(() =>
                                taskEither.fromIO(() =>
                                  props.onSelectOfferSocialInsurance()
                                )
                              )
                            )()
                    )
                  );
                }}
              />
            </Stack>
          </Stack>
        </Card>
      ))
    );

  const renderBackButton = (
    <ContentRow type="full">
      <Box grow column={isMobileLayout}>
        <Button
          variant="secondary"
          size="default"
          label={formatMessage("StandardLoan.CounterOffer.backButton")}
          action={props.onBack}
          disabled={false}
        />
      </Box>
    </ContentRow>
  );
  return pipe(
    props.offerState === "RECEIVED",
    boolean.fold(
      () => (
        <Stack column units={10}>
          <FeedbackBlock
            size="large"
            type="negative"
            heading={formatMessage("StandardLoan.CounterOffer.title")}
            subheading={option.some(
              formatMessage("StandardLoan.CounterOffer.timeout")
            )}
            actions={[]}
          />
          {renderBackButton}
        </Stack>
      ),
      () =>
        pipe(
          counterOffer,
          remoteData.fold(
            constNull,
            () => (
              <Stack column units={10}>
                <FeedbackBlock
                  size="large"
                  type="negative"
                  heading={formatMessage("StandardLoan.Rejection.Offers.title")}
                  subheading={option.some(
                    formatMessage("StandardLoan.Rejection.Offers.description")
                  )}
                  actions={[]}
                />
                <></>
              </Stack>
            ),
            ({ offers }) => (
              <Stack column units={10}>
                <>
                  <FeedbackBlock
                    size="large"
                    type="info"
                    heading={pipe(
                      offers[0].offerType,
                      option.fold(
                        () => formatMessage("StandardLoan.CounterOffer.title"),
                        val =>
                          val === "COUNTER_OFFER"
                            ? formatMessage("StandardLoan.CounterOffer.title")
                            : formatMessage(
                                `StandardLoan.CounterOffer.${val}.title` as LocaleKey
                              )
                      )
                    )}
                    subheading={option.some(
                      pipe(
                        offers[0].offerType,
                        option.fold(
                          () =>
                            formatMessage(
                              "StandardLoan.CounterOffer.description"
                            ),
                          val =>
                            val === "COUNTER_OFFER"
                              ? formatMessage(
                                  "StandardLoan.CounterOffer.description"
                                )
                              : formatMessage(
                                  `StandardLoan.CounterOffer.${val}.description` as LocaleKey
                                )
                        )
                      )
                    )}
                    actions={[]}
                  />
                  <Box hAlignContent="center">{renderOffers(offers)}</Box>
                </>
                {pipe(
                  offers[0].offerType,
                  option.fold(
                    () => null,
                    val =>
                      val === "COUNTER_OFFER"
                        ? null
                        : formatMessage(
                            "StandardLoan.CounterOffer.backButtonComplianceText"
                          )
                  )
                )}
                {renderBackButton}
              </Stack>
            )
          )
        )
    )
  );
}
